<template>
  <div>
    <el-card class="box-card">
      <div
          id="device-general"
          slot="header"
          class="clearfix">
        <span class="header-item">{{ $t('device.general') }}</span>
        <span class="ring-card__header-buttons flex flex-row">

          <span
              v-if="http.warranty.loaded && delayedFulfillmentEnabled"
              class="oow-text"
              style="display: flex; align-items: center;">
            <StatusTag
                style="font-size: 16px;"
                :status-message="getFulfillmentStatus()"
                :disable-tip="!showFulfillmentReason"
                :is-error="getFulfillmentStatus() !== 'Fulfillment Standard'">
              <template v-slot:popover-content>
                <FulfillmentReasons :reason="getFulfillmentReason()" />
              </template>
            </StatusTag>
          </span>

          <span class="flex flex-col">
            <span
                v-if="http.warranty.loaded"
                class="oow-text"
                style="display: flex; align-items: center;">
              <StatusTag
                  style="font-size: 16px;"
                  :status-message="warrantyInfo.status"
                  :is-error="!warrantyInfo.in_warranty">
                <template v-slot:popover-content>
                  <WarrantyInfo :data="warrantyInfo" />
                </template>
              </StatusTag>
            </span>
          </span>

          <EligibilityTags
              v-if="coverage.eligibility"
              style="margin-right: 7px; font-size: 16px;"
              :eligibility="coverage.eligibility" />

          <PLCRequestDropdown
              :eligibility="coverage.eligibility"
              @plc-request="handlePLCRequest" />

          <span
              class="header-action no-actions with-border-left custom-status-text">
            <b>{{ plcState | capitalize }}</b>
          </span>
        </span>
      </div>
      <div
          v-loading="loading"
          class="card-content">
        <el-row :gutter="20">
          <el-col
              :span="8"
              class="with-border">
            <div class="container">
              <div
                  v-if="device.name"
                  class="device-row-details">
                <div class="ring-card__input-label">
                  Device Name
                </div>
                <p class="ring-card__input">
                  {{ device.name }}
                </p>
              </div>
              <div
                  v-if="device.mac_address"
                  class="device-row-details">
                <div class="ring-card__input-label">
                  MAC ID
                </div>
                <p class="ring-card__input">
                  {{ device.mac_address | uppercase }}
                </p>
              </div>

              <div
                  v-if="!device.mac_address && device.hardware_id"
                  class="device-row-details">
                <div class="ring-card__input-label">
                  Hardware ID
                </div>
                <p class="ring-card__input">
                  {{ device.hardware_id | uppercase }}
                </p>
              </div>

              <div v-if="hasRingSolutionsUrl(device)">
                <AlarmDesktopLink :url="device.ring_solutions_url" />
              </div>
            </div>

            <div class="device-row-details">
              <div class="ring-card__input-label">
                Retailer Name
              </div>
              <p class="ring-card__input">
                {{ retailerName }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Retailer Trial Period
              </div>
              <p class="ring-card__input">
                {{ device.retailer ? device.retailer.trial_days + " days" : 'N/A' }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Retailer XLINE
              </div>
              <p class="ring-card__input">
                {{ retailerXLine }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Product
              </div>
              <p class="ring-card__input">
                {{ productDescription }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Market
              </div>
              <p class="ring-card__input">
                {{ deviceLocation().market | capitalize }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Color
              </div>
              <p class="ring-card__input">
                {{ NA(device.profile.color) }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Current Stock
              </div>
              <p class="ring-card__input">
                {{ NA(device.profile.current_stock) }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Serial number
              </div>
              <p class="ring-card__input">
                {{ NA(device.profile.serial_number) }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Kind
              </div>
              <p class="ring-card__input">
                {{ device.type | capitalize }}
              </p>
            </div>
          </el-col>
          <el-col
              :span="8"
              class="with-border">
            <div class="device-row-details">
              <div class="ring-card__input-label">
                State
              </div>
              <p class="ring-card__input">
                <el-popover
                    placement="top-start"
                    width="200"
                    trigger="hover">
                  <span
                      v-if="device.deleted_at !== undefined"
                      class="offline-device__icon">Deleted on {{ device.deleted_at | moment("MMMM Do YYYY, h:mm a") }}</span>
                  <span
                      v-else-if="device.deactivated_at !== undefined"
                      class="offline-device__icon">Deactivated on {{ device.deactivated_at | moment("MMMM Do YYYY, h:mm a") }}</span>
                  <span
                      v-else
                      class="online-device__icon">Activated on {{ device.created_at | moment("MMMM Do YYYY, h:mm a") }}</span>
                  <span slot="reference">
                    <span
                        v-if="device.deleted_at !== undefined"
                        class="offline-device__icon">
                      <el-tag
                          size="medium"
                          class="info-tag"
                          type="info">
                        Deleted
                      </el-tag>
                    </span>
                    <span
                        v-else-if="device.deactivated_at !== undefined"
                        class="offline-device__icon">
                      <el-tag
                          size="medium"
                          class="info-tag"
                          type="info">
                        Deactivated
                      </el-tag>
                    </span>
                    <span
                        v-else
                        class="online-device__icon">
                      <el-tag
                          size="medium"
                          class="info-tag"
                          type="info">
                        Active
                      </el-tag>
                    </span>
                  </span>
                </el-popover>
              </p>
            </div>

            <div class="device-row-details">
              <div class="ring-card__input-label">
                Status
              </div>
              <p class="ring-card__input">
                <font-awesome-icon
                    v-if="gettingDeviceStatus"
                    class="fetching-device-status"
                    icon="circle-notch"
                    spin />

                <font-awesome-icon
                    v-else
                    icon="circle"
                    :class="connectedDesc" />
                {{ connectedDesc | capitalize }}

                <el-button
                    v-if="connectedDesc !== 'fetching'"
                    style="padding: 0;"
                    type="text"
                    @click="getDeviceStatus">
                  <i class="el-icon-refresh"></i>
                </el-button>
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                AI Active Warnings
              </div>
              <p class="ring-card__input">
                <el-tag
                    class="info-tag"
                    size="medium"
                    type="info">
                  {{ AIActiveWarningsEnabled ? 'Active' : 'Inactive' }}
                </el-tag>
              </p>
            </div>

            <div class="device-row-details">
              <div class="ring-card__input-label">
                Last Kept Alive
              </div>
              <p class="ring-card__input">
                <time-zone-formatter :current-time="lastKeepAlive" />
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Manufactured At
              </div>
              <p class="ring-card__input">
                {{ formattedManufacturedAt }}
              </p>
            </div>
            <div class="device-row-details">
              <div class="ring-card__input-label">
                Network Name
              </div>
              <p class="ring-card__input">
                {{ networkName }}
              </p>
            </div>
            <div
                v-if="canReboot(device.type)"
                class="device-row-details">
              <div class="ring-card__input-label">
                Reboot
              </div>
              <p class="ring-card__input">
                <el-button
                    :disabled="!isOnlineDevice"
                    style="margin-top: 10px"
                    type="success"
                    size="medium"
                    :icon="rebootBtnIcon"
                    plain
                    @click="rebootDevice" />
              </p>
            </div>

            <div class="device_row_details">
              <div class="ring-card__input-label">
                {{ isStolen ? 'Device Reported': 'Report Stolen' }}
              </div>
              <div class="ring-card__input">
                <el-tooltip
                    :disabled="canToggleStolen"
                    content="Admin access required"
                    placement="top"
                    effect="light"
                    data-test-id="stolen-tooltip">
                  <el-popover
                      v-model="stolenModal"
                      placement="top"
                      width="275">
                    <h5 style="text-align: center; margin: 0px 10px 0px;">
                      Report Stolen Modal
                    </h5>
                    <p style="text-align: center; margin: 10px 0px; font-size: 13px;">
                      {{ device.profile.stolen ? 'Are you sure you want remove the stolen flag?' : 'Are you sure you want to report this device as stolen?' }}
                    </p>
                    <div style="text-align: right; margin: 0">
                      <el-button
                          :disabled="reportStolenLoading"
                          size="mini"
                          type="text"
                          @click="stolenModal = false">
                        Cancel
                      </el-button>
                      <el-button
                          :disabled="reportStolenLoading"
                          size="mini"
                          type="danger"
                          @click="confirmReportStolen()">
                        Confirm
                      </el-button>
                    </div>


                    <el-button
                        slot="reference"
                        :disabled="!canToggleStolen"
                        style="margin-top: 10px"
                        :type="isStolen ? 'danger' : 'info'"
                        plain
                        data-test-id="stolen-button"
                        icon="el-icon-message-solid" />
                  </el-popover>
                </el-tooltip>
              </div>
            </div>
          </el-col>
          <el-col
              v-if="isFirstClassDevice(device) || isCellularParameters"
              :span="8"
              class="with-border">
            <div v-if="isFirstClassDevice(device)">
              <DiagnosticHealth
                  v-if="!isTrackerDevice"
                  :report-time-stamp="lastStatusReport.timestamp"
                  :diagnostic="diagnostic"
                  :health="safeDeviceHealth" />
              <BatteryStatus
                  v-if="isTrackerDevice"
                  :battery="device.connection_status" />
            </div>

            <!-- Jaws cellular data -->
            <cellular-data
                v-if="isCellularParameters"
                :doorbot-id="device.doorbot_id" />
          </el-col>
        </el-row>
      </div>
      <!-- NEW REPLACEMENT FORM DIALOG-->
      <replacement-form
          v-if="device && newReplacementFormVisible"
          ref="replacement"
          :visible.sync="newReplacementFormVisible"
          :device="device"
          :eligibility="coverage.eligibility"
          :is-supervisor="isSupervisor" />

      <!-- PARTS FORM DIALOG-->
      <parts-modal-form
          v-if="device"
          ref="parts"
          :device="device" />

      <!-- REFUND FORM DIALOG-->
      <refund-modal-form
          v-if="device"
          ref="refund"
          :device="device" />

      <!-- OOW DIALOG-->
      <out-of-warranty-warning-modal
          ref="outOfWarrantyWarningModal" />
    </el-card>
  </div>
</template>

<script>
import axios from "axios";
import ReplacementForm from "../ReplacementWidget/ReplacementForm.vue";
import ReplacementModalForm from "@/containers/CustomerRequest/ReplacementModalForm";
import RefundModalForm from "@/containers/CustomerRequest/RefundModalForm";
import PartsModalForm from "@/containers/CustomerRequest/PartsModalForm";
import OutOfWarrantyWarningModal from "@/containers/CustomerRequest/OutOfWarrantyWarningModal";
import DiagnosticHealth from "@/components/Shared/Device/DiagnosticHealth";
import BatteryStatus from "@/components/Shared/Device/BatteryStatus";
import CellularData from "./CellularData";
import AlarmDesktopLink from "./Shared/AlarmDesktopLink";
import deviceUtils from "@/mixins/deviceUtils";
import permission from "@/mixins/permission";

import { get, startCase } from "lodash";
import { createNamespacedHelpers as h } from "vuex";
import WarrantyInfo from "@/components/Shared/WarrantyInfo.vue";
import {
  DeviceWarrantyAndEligibilityPolicy,
  Warranty,
} from "@/helpers/DeviceWarrantyAndEligibilityPolicy";
import EligibilityTags from "@/components/Shared/EligibilityTags.vue";
import PLCRequestDropdown from "@/components/Shared/PLCRequestDropdown.vue";
import TimeZoneFormatter from "@/helpers/TimeZoneFormatterHelper";
import FulfillmentReasons from "@/components/Shared/FulfillmentReasons.vue";
import StatusTag from "@/components/Shared/StatusTag.vue";

const { mapGetters: deviceGetters, mapActions: deviceActions } = h("db/device");

const { mapGetters: accountGetters } = h("db/account");
const { mapGetters: sharedGetters } = h("shared");

export default {
  name: "DeviceGeneral",
  components: {
    PLCRequestDropdown,
    EligibilityTags,
    WarrantyInfo,
    ReplacementForm,
    AlarmDesktopLink,
    CellularData,
    ReplacementModalForm,
    RefundModalForm,
    PartsModalForm,
    OutOfWarrantyWarningModal,
    DiagnosticHealth,
    BatteryStatus,
    FulfillmentReasons,
    StatusTag,
  },
  mixins: [deviceUtils],
  props: {
    device: {
      required: true,
      type: Object,
    },
    lastStatusReport: {
      required: true,
      type: Object,
    },
    loading: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
  },
  data() {
    return {
      firstSetupDate: "",
      newReplacementFormVisible: false,
      warrantyEndsAt: "",
      oowReason: "",
      reboot: { working: false },
      stolenModal: false,
      reportStolenLoading: false,
      requestForms: {
        replacement: { open: false },
        refund: { open: false },
        parts: { open: false },
      },
      coverage: { warranty: {}, eligibility: {} },
      http: {
        warranty: {
          fetching: false,
          loaded: false,
        },
      },
    };
  },
  computed: {
    ...sharedGetters([
      "userHasPermission",
      "markets",
      "delayedFulfillmentEnabled",
      "delayedFulfillmentTipsEnabled",
    ]),
    ...deviceGetters([
      "AIActiveWarningsEnabled",
      "canReboot",
      "macId",
      "connected",
      "tags",
      "notes",
      "diagnostic",
      "hasZoneEnabled",
      "hasLowBatteryLevel",
      "deviceOptions",
    ]),
    ...accountGetters(["customer", "piiAuthorized"]),
    canToggleStolen() {
      return this.userHasPermission({
        permission: "change-stolen-status",
        on: "DeviceStolenStatusControl",
      });
    },
    isStolen() {
      return this.device.profile.stolen;
    },
    warrantyInfo() {
      return this.coverage?.warranty || {};
    },
    productDescription() {
      return get(this.device, "profile.product.description", "ø");
    },
    safeDeviceHealth() {
      // Support device health inside data for QA/Dev and inside last_status_report for Prod
      const deviceHealth =
        get(this.device, "last_status_report.data.device_health") ||
        get(this.device, "last_status_report.device_health");
      return deviceHealth || null;
    },
    isSupervisor() {
      return this.$store.getters["auth/isSupervisor"];
    },
    retailerName() {
      return get(this.device, "retailer.name", "N/A");
    },
    retailerXLine() {
      let xLine = get(this.device, "retailer.xline", false);
      return Boolean(xLine) ? "Yes" : "No";
    },
    plcState() {
      return get(this.device, "profile.plc_state", "Unknown");
    },
    rebootBtnIcon() {
      return this.reboot.working ? "el-icon-loading" : "el-icon-switch-button";
    },
    isTrackerDevice() {
      return /vehicle_brody_v1|tracker_buddy/.test(this.device.type);
    },
    isInWarranty() {
      return !!this.coverage.eligibility?.isInWarranty;
    },
    OOWFlow() {
      return !!this.userHasPermission({
        permission: "OOWFlow",
        on: "NewUI",
      });
    },
    newReplacementFlow() {
      return !!this.userHasPermission({
        permission: "newReplacementFlow",
        on: "NewUI",
      });
    },
    showFulfillmentReason() {
      return (
        this.delayedFulfillmentTipsEnabled &&
        this.coverage.eligibility?.isDelayedFulfillment
      );
    },
    connectedDesc() {
      let desc;
      switch (this.connected) {
        case undefined:
          desc = "fetching";
          break;
        case true:
          desc = "online";
          break;
        case false:
          desc = "offline";
          break;
        case null:
          desc = "unknown";
      }
      return desc;
    },
    isOnlineDevice() {
      return this.connectedDesc === "online";
    },
    gettingDeviceStatus() {
      return this.connected === undefined;
    },
    networkName() {
      return get(
        this.device,
        "last_setup.metadata.network_config_ssid",
        this.hostname || "unknown"
      );
    },
    hostname() {
      if (this.isTrackerDevice) {
        return get(this.device, "connection_status.hostname", false);
      } else {
        return get(this.device, "last_keep_alive.hostname", false);
      }
    },
    lastKeepAlive() {
      let keepAlive = get(
        this.device,
        "connection_status.last_keep_alive",
        null
      );
      if (keepAlive) return Number(keepAlive) * 1000;
      else return "";
    },
    manufacturedAt() {
      const rawValue = get(this.device, "profile.manufactured_at", null);

      // If the value is a number (timestamp) and appears to be in seconds (not milliseconds)
      // A simple heuristic: if the timestamp is less than 10000000000, it's likely in seconds
      if (typeof rawValue === "number" && rawValue < 10000000000) {
        return rawValue * 1000; // Convert seconds to milliseconds
      }

      return rawValue;
    },
    formattedManufacturedAt() {
      const manufacturedAt = this.manufacturedAt;
      if (!manufacturedAt) return "N/A";
      return TimeZoneFormatter.formattedTime(manufacturedAt);
    },
    isCellularParameters() {
      const deviceType = get(this.device, "type", "");
      return deviceType === "rccs_1p_jaws";
    },
  },
  watch: {
    device: {
      handler(device) {
        const shouldLoadWarranty =
          device?.owner_id &&
          !this.http.warranty.loaded &&
          !this.http.warranty.fetching;

        if (shouldLoadWarranty) {
          this.fetchDevicesWarrantyAndEligibility();
        }
      },
    },
  },
  methods: {
    resetCoverage() {
      this.coverage = { warranty: {}, eligibility: {} };
    },
    async fetchDevicesWarrantyAndEligibility() {
      const device_id =
        this.device.mac_address || this.device.profile?.device_id;
      const doorbot_id = this.device.doorbot_id;

      this.http.warranty.fetching = true;
      this.resetCoverage();
      try {
        const coverage = await this.getWarrantyAndEligibilityStatus({
          category: "device",
          device_id,
          doorbot_id,
          customerId: this.customer.id,
          locationId: this.device.location_id,
        });

        this.coverage.warranty = coverage.warranty || new Warranty().toJson();
        if (this.device.deleted_at || this.device.deactivated_at) {
          const warranty = new Warranty();
          this.coverage.warranty = warranty.toJson();
        }

        this.coverage.eligibility =
          DeviceWarrantyAndEligibilityPolicy.create(coverage);

        this.http.warranty.loaded = true;
      } catch (error) {
        this.coverage.eligibility = DeviceWarrantyAndEligibilityPolicy.create(
          {}
        );
        this.coverage.warranty = new Warranty().toJson();

        this.$notify({
          title: "Warranty/Eligibility not available",
          message:
            "An error occurred verifying warranty status for this device.",
          type: "error",
        });
      }
      this.$nextTick(() => {
        this.http.warranty.fetching = false;
      });
    },
    NA(value) {
      return [undefined, "undefined", null].includes(value) ? "N/A" : value;
    },
    ...deviceActions([
      "getDeviceStatus",
      "reportStolen",
      "getWarrantyAndEligibilityStatus",
    ]),
    deviceLocation() {
      const locations = this.$store.getters["db/account/locations"];

      return (
        locations.filter(
          (l) => l.location_id === this.device.location_id
        )[0] || {
          market: "n/a",
        }
      );
    },
    handlePLCRequest(command) {
      switch (command) {
        case "replace":
          this.openNewReplacement();
          break;
        case "parts":
          this.openParts();
          break;
        case "refund":
          this.openRefund();
          break;
      }
    },
    openRefund() {
      const { eligibility } = this.coverage;
      const { refund } = eligibility;
      const needByPass = refund.isRestricted && refund.isUserAllowed;

      const openRefundForm = (oowBypassReason = null) => {
        this.$nextTick(() => {
          this.$refs.refund.openForm(oowBypassReason);
        });
      };

      if (needByPass) {
        this.$nextTick(() => {
          this.$refs.outOfWarrantyWarningModal.show(
            "refund",
            eligibility,
            openRefundForm
          );
        });
      } else if (refund.isUserAllowed) {
        openRefundForm();
      }
    },
    openNewReplacement() {
      const { eligibility, warranty } = this.coverage;
      const { replacement } = eligibility;

      if (replacement.isUserAllowed) {
        const {
          activation_date: firstSetupDate,
          standard_expiration: warrantyEndsAt,
          notes: oowReason,
        } = warranty;

        this.firstSetupDate = firstSetupDate;
        this.warrantyEndsAt = warrantyEndsAt;
        this.oowReason = oowReason;
        this.newReplacementFormVisible = true;
      }
    },
    openParts() {
      const { eligibility } = this.coverage;
      const { parts } = eligibility;
      const needByPass = parts.isRestricted && parts.isUserAllowed;

      const openPartsForm = (oowBypassReason = null) => {
        this.$nextTick(() => {
          this.$refs.parts.openForm(oowBypassReason);
        });
      };

      if (needByPass) {
        this.$nextTick(() => {
          this.$refs.outOfWarrantyWarningModal.show(
            "parts",
            eligibility,
            openPartsForm
          );
        });
      } else if (parts.isUserAllowed) {
        openPartsForm();
      }
    },
    openPartsForm(oowBypassReason = null) {
      this.$nextTick(() => {
        this.$refs.parts.openForm(oowBypassReason);
      });
    },
    notify(form) {
      this.$notify({
        message: `${form} Form closed`,
        type: "success",
      });
    },
    displayStolenToggleMessage() {
      let message;
      if (this.isStolen) {
        message = "Device reported stolen successfully";
      } else {
        message = "Device stolen status reset successfully";
      }
      this.$notify({
        message: message,
        type: "success",
      });
    },
    confirmReportStolen() {
      this.reportStolenLoading = true;
      this.reportStolen({ doorbotId: this.device.doorbot_id })
        .then(() => {
          this.reportStolenLoading = false;
          this.stolenModal = false;
          this.displayStolenToggleMessage();
        })
        .catch(() => {
          this.$notify({
            message:
              "Something went wrong, please try again later or contact support.",
            type: "error",
          });
        });
    },
    rebootDevice() {
      this.reboot.working = true;
      let type = this.device.type;
      let id = this.device.doorbot_id;
      axios
        .post(`v2/devices/${id}/reboot.json`, { device_kind: type })
        .then((response) => {
          this.$notify({
            title: "Reboot",
            message: "Request completed successfully",
            type: "success",
          });
        })
        .catch((response) => {
          this.$notify({
            title: "Reboot",
            message: "Request completed with NO success",
            type: "error",
          });
        })
        .finally((nothing) => {
          this.reboot.working = false;
        });
    },
    isFirstClassDevice(device) {
      return (
        /(chime)|(base_station)|(beams)|(three)|(echo)|(jaws)/.test(
          device.type
        ) === false
      );
    },
    getFulfillmentStatus() {
      return this.coverage.eligibility.isDelayedFulfillment
        ? "Fulfillment Delayed"
        : "Fulfillment Standard";
    },
    getFulfillmentReason() {
      return get(this.coverage, "eligibility.delayedFulfillmentReason", "");
    },
  },
};
</script>

<style>
/* Dynamic settings fix */
#device-settings .el-card__body {
  margin: 0 !important;
  padding: 20px !important;
}

#device-settings .el-card {
  margin-bottom: 20px !important;
}
.oow-text {
  margin: 0 1rem 0 1rem;
  display: inline-flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  &span {
    flex: auto;
  }
}
.choose_plc_request_button {
  color: #1476ad;
}
.plc_request_item {
  width: 260px;
  font-family: "Equip", sans-serif !important;
  font-style: normal;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #424242 !important;
  padding: 8px 16px !important;
}

.plc-request-dropdown {
  margin-top: 0 !important;
  padding: 8px 0 !important;
  border: 1px solid #81919f !important;
}
.disabled {
  color: #81919f !important;
}

.plc-request-dropdown .popper__arrow,
.plc-request-dropdown .popper__arrow::after {
  display: none !important;
}

.plc_request_item:hover {
  background: #eff8fc;
}
</style>

<style scoped lang="scss">
.device-row-details {
  margin-bottom: 15px;
  p {
    margin: 0px;
    font-size: 17px;
  }
  &:last-child {
    margin-bottom: 0px;
  }
  .fetching-device-status {
    color: $--color-curious-blue;
  }
}

.el-col.with-border {
  min-height: 300px;
  padding: 0px 40px !important;
  border-right: thin solid $--color-silver;
  &:last-child {
    border-right: none;
  }
}

.info-tag {
  font-size: 17px;
}

.online {
  color: #67c23a;
}

.offline {
  color: #f56c6c;
}

.unknown {
  color: $--color-dusty-gray;
}

.new-disabled {
  color: #81919f !important;
}

.disabled {
  color: #f3f3f3 !important;
}
.no_data {
  color: #e6a23c;
}

.active {
  color: #67c23a;
  font-weight: 300;
}

.replace_process {
  color: #f56c6c;
  font-weight: 300;
}

.bottom-row {
  position: absolute;
  bottom: 10px;
  width: inherit;
}

hr {
  border: 0;
  height: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}
.device-stolen {
  color: #f56c6c !important;
}

.header-action.with-border-left {
  .old-ui & {
    border-left: 1px #fff solid;
  }
  .new-ui & {
    border-left: 1px $--color-primary solid;
  }
  border-radius: 0px;

  margin-left: 20px;
  padding-left: 20px;
}

.header-action.no-actions {
  pointer-events: none;
  text-decoration: none;
  font-weight: 100;
}
.header-action.disabled {
  text-decoration: none;
  opacity: 0.5;
}
.header-action {
  float: right;
  height: 62px;
}

.custom-status-text {
  font-size: 20px;
}

.container {
  justify-content: space-between;
  margin-bottom: 10px;
}

// NEW OUT OF WARRANTY BUTTON
.out-of-warranty-buttons button {
  color: #d71b00 !important;
  padding: 10px !important;
  border-radius: 4px;
  position: relative;
  &:hover {
    border: 1px solid #eaeaea;
    box-shadow: 0 0 5px #cccccc;
  }
}
</style>
